import { makeAutoObservable, runInAction } from 'mobx'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { module2Store } from './module2Store'
import { subscriptionStore } from './subscriptionStore'

/**
 * Client half of roleplay metering for B2C users.
 *
 * The server reserves minutes when a roleplay starts and tells the client how long it may
 * run; the existing countdown in module2Store stays the mechanism the user sees, this
 * store only decides where it starts and keeps the server informed. For a B2B user the
 * server answers "not metered" and nothing here runs.
 */

export const INSUFFICIENT_MINUTES = 'INSUFFICIENT_MINUTES'

/** A roleplay does not start on less than this. Same floor as the server's. */
export const MIN_START_SECONDS = 60

const HEARTBEAT_INTERVAL_MS = 20_000
/** Drift below this is left alone so the countdown does not visibly jump. */
const DRIFT_TOLERANCE_SECONDS = 3

export type RolePlayMode = 'text' | 'voice' | 'video' | 'camera'

const START = gql`
  mutation startRoleplaySession($moduleId: Int!, $mode: String!) {
    startRoleplaySession(moduleId: $moduleId, mode: $mode) {
      metered
      sessionId
      allowedSeconds
      balanceSeconds
    }
  }
`

const HEARTBEAT = gql`
  mutation heartbeatRoleplaySession($sessionId: Int!) {
    heartbeatRoleplaySession(sessionId: $sessionId) {
      open
      remainingSeconds
    }
  }
`

// A plain string as well: the pagehide request is a raw fetch, not graphql-request.
const END_MUTATION = `
  mutation endRoleplaySession($sessionId: Int!) {
    endRoleplaySession(sessionId: $sessionId) {
      success
      moduleId
      balanceSeconds
    }
  }
`

const GET_OFFER = gql`
  query getMinuteOffer {
    getMinuteOffer {
      purchasable
      totalSeconds
      packages {
        id
        name
        seconds
        priceAmount
        currency
      }
    }
  }
`

const CREATE_CHECKOUT = gql`
  mutation createMinuteCheckout($packageId: Int!) {
    createMinuteCheckout(packageId: $packageId) {
      url
    }
  }
`

const CONFIRM_PURCHASE = gql`
  mutation confirmMinutePurchase($checkoutSessionId: String!) {
    confirmMinutePurchase(checkoutSessionId: $checkoutSessionId) {
      status
      seconds
      organisationName
      totalSeconds
    }
  }
`

const GET_BALANCES = gql`
  query getUserModuleBalances {
    getUserModules {
      id
      metered
      balanceSeconds
    }
  }
`

export const isInsufficientMinutes = (error: any): boolean => error?.response?.errors?.some((e: any) => e?.extensions?.code === INSUFFICIENT_MINUTES) ?? false

/** The refusal code the server put in `extensions`, if any. */
export const errorCodeOf = (error: any): string | null => error?.response?.errors?.find((e: any) => e?.extensions?.code)?.extensions?.code ?? null

export interface MinutePackage {
  id: number
  name: string
  seconds: number
  /** Minor units of `currency`: 4900 is 49.00 EUR. */
  priceAmount: number
  currency: string
}

/** The organisation's top-ups. Their minutes work on every module of the user's plan. */
export interface MinuteOffer {
  purchasable: boolean
  totalSeconds: number
  packages: MinutePackage[]
}

/**
 * Where the offer modal stands after a return from Stripe.
 *
 * `added` is reached by this store, not by the reaction in Module2Page: that one fires
 * only when the out-of-minutes flag changes, which it does not for a top-up.
 */
export type PurchaseState = 'idle' | 'confirming' | 'added' | 'stillPending' | 'cancelled' | 'error'

/** Stripe redirects back as soon as the card is charged; the webhook may be a moment behind. */
const CONFIRM_DELAYS_MS = [2000, 4000, 8000, 16000]

export interface StartedSession {
  metered: boolean
  sessionId: number | null
  allowedSeconds: number
}

class MeteringStore {
  sessionId: number | null = null
  allowedSeconds = 0
  /** Client clock when the start response arrived; the countdown is anchored to it. */
  startAnchorMs = 0
  /** The "your minutes are used up" modal on the main screen. */
  offerOpen = false

  /** The organisation's top-ups. Null until the first load. */
  offer: MinuteOffer | null = null
  offerLoading = false
  /** A refusal code from the server (PACKAGE_UNAVAILABLE, TOO_MANY_CHECKOUTS, ...). */
  offerError: string | null = null
  /** Which package's buy button is waiting for its redirect. */
  buyingPackageId: number | null = null

  purchaseState: PurchaseState = 'idle'
  /** Set when the minutes went to an organisation the buyer has since left. */
  purchaseOrganisationName: string | null = null

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private confirmTimers: ReturnType<typeof setTimeout>[] = []

  constructor() {
    makeAutoObservable<MeteringStore, 'heartbeatTimer' | 'confirmTimers'>(this, { heartbeatTimer: false, confirmTimers: false }, { autoBind: true })
  }

  get hasOpenSession() {
    return this.sessionId !== null
  }

  /** Seconds the open session has left: allowed, less what connecting has already cost. */
  remainingNow(): number {
    const elapsed = Math.floor((Date.now() - this.startAnchorMs) / 1000)
    return Math.max(0, this.allowedSeconds - elapsed)
  }

  openOffer() {
    this.offerOpen = true
  }

  dismissOffer() {
    this.offerOpen = false
    this.offerError = null
    this.buyingPackageId = null
    if (this.purchaseState !== 'confirming') this.clearPurchase()
  }

  /** Load the organisation's top-ups. Called when the modal opens. */
  async loadOffer(): Promise<void> {
    this.offerLoading = true
    this.offerError = null
    try {
      const response = await authorizedGraphQLRequest<{ getMinuteOffer: MinuteOffer }>(GET_OFFER)
      runInAction(() => {
        this.offer = response.getMinuteOffer
      })
    } catch (error) {
      console.error('Failed to load the minute offer:', error)
      runInAction(() => {
        this.offer = null
      })
    } finally {
      runInAction(() => {
        this.offerLoading = false
      })
    }
  }

  /**
   * Start a checkout and hand the browser to Stripe. The only thing sent is a package
   * id: the price, the minutes and the currency are the server's.
   */
  async buy(packageId: number): Promise<void> {
    this.buyingPackageId = packageId
    this.offerError = null
    try {
      const response = await authorizedGraphQLRequest<{ createMinuteCheckout: { url: string } }>(CREATE_CHECKOUT, { packageId })
      const url = response.createMinuteCheckout?.url
      if (!url) throw new Error('No checkout URL')
      window.location.assign(url)
    } catch (error) {
      console.error('Failed to start the checkout:', error)
      runInAction(() => {
        this.offerError = errorCodeOf(error) ?? 'CHECKOUT_FAILED'
        this.buyingPackageId = null
      })
    }
  }

  /**
   * Back from Stripe. Asks the server to confirm now and, while the answer is still
   * pending, again after 2, 4, 8 and 16 seconds: card payments are synchronous, so
   * this normally finds the purchase already fulfilled by the webhook, and otherwise
   * fulfils it itself.
   */
  async confirmReturn(checkoutSessionId: string): Promise<void> {
    this.clearConfirmTimers()
    this.purchaseState = 'confirming'
    this.purchaseOrganisationName = null
    this.offerOpen = true

    const attempt = async (): Promise<boolean> => {
      try {
        const response = await authorizedGraphQLRequest<{
          confirmMinutePurchase: { status: string; seconds: number; organisationName: string | null; totalSeconds: number }
        }>(CONFIRM_PURCHASE, { checkoutSessionId })
        const result = response.confirmMinutePurchase

        if (result.status === 'paid') {
          runInAction(() => {
            this.purchaseState = 'added'
            this.purchaseOrganisationName = result.organisationName
          })
          await this.refreshBalances()
          // The Module2Page reaction closes the modal only when the out-of-minutes
          // flag changes, which a top-up does not do, so this closes it itself.
          if (!result.organisationName) {
            runInAction(() => {
              this.offerOpen = false
              this.purchaseState = 'idle'
            })
          }
          return true
        }

        if (result.status === 'failed' || result.status === 'expired') {
          runInAction(() => {
            this.purchaseState = 'error'
          })
          return true
        }

        return false
      } catch (error) {
        console.error('Failed to confirm the purchase:', error)
        runInAction(() => {
          this.purchaseState = 'error'
        })
        return true
      }
    }

    if (await attempt()) return

    for (const delay of CONFIRM_DELAYS_MS) {
      const settled = await new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => {
          attempt().then(resolve, () => resolve(true))
        }, delay)
        this.confirmTimers.push(timer)
      })
      if (settled) return
    }

    // The money is taken and the minutes will arrive; nothing here needs to block.
    runInAction(() => {
      this.purchaseState = 'stillPending'
    })
  }

  /** The buyer came back from Stripe without paying. */
  cancelledReturn() {
    this.clearConfirmTimers()
    this.purchaseState = 'cancelled'
    this.offerOpen = true
  }

  clearPurchase() {
    this.clearConfirmTimers()
    this.purchaseState = 'idle'
    this.purchaseOrganisationName = null
  }

  private clearConfirmTimers() {
    for (const timer of this.confirmTimers) clearTimeout(timer)
    this.confirmTimers = []
  }

  /**
   * Ask the server to start a session, before anything connects: the voice and avatar
   * tokens are refused for a B2C user without one. Throws on INSUFFICIENT_MINUTES
   * (check with `isInsufficientMinutes`) and on any other failure.
   */
  async start(moduleId: number, mode: RolePlayMode): Promise<StartedSession> {
    // A camera role play (user-facing "Video") is a voice call plus the learner's own
    // camera; the server knows only text, voice and video, so it is recorded as video
    // (openspec add-camera-roleplay-mode, design D5).
    const serverMode = mode === 'camera' ? 'video' : mode
    const response = await authorizedGraphQLRequest<{ startRoleplaySession: StartedSession }>(START, { moduleId, mode: serverMode })
    const started = response.startRoleplaySession

    if (started.metered && started.sessionId !== null) {
      runInAction(() => {
        this.sessionId = started.sessionId
        this.allowedSeconds = started.allowedSeconds
        this.startAnchorMs = Date.now()
      })
      this.startHeartbeat()
      window.addEventListener('pagehide', this.endOnPageHide)
    }

    return started
  }

  /**
   * End the open session, if any, and pick up the balance it leaves. Safe to call from
   * every exit path at once: only the first call finds a session to end.
   */
  async end(): Promise<void> {
    const sessionId = this.sessionId
    if (sessionId === null) return

    this.clearSession()

    try {
      await authorizedGraphQLRequest(END_MUTATION, { sessionId })
    } catch (error) {
      // The server's reaper closes it if this never arrives.
      console.error('Failed to end roleplay session:', error)
    }

    await this.refreshBalances()
  }

  /**
   * Re-read the per-module balances (which decide whether a roleplay can start) and
   * the subscription with its total (which the sidebar shows).
   */
  async refreshBalances(): Promise<void> {
    try {
      const [response] = await Promise.all([
        authorizedGraphQLRequest<{ getUserModules: { id: number; metered: boolean | null; balanceSeconds: number | null }[] }>(GET_BALANCES),
        subscriptionStore.refresh(),
      ])
      module2Store.setBalances(response.getUserModules || [])
    } catch (error) {
      console.error('Failed to refresh minute balances:', error)
    }
  }

  private clearSession() {
    this.sessionId = null
    this.allowedSeconds = 0
    this.startAnchorMs = 0
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    window.removeEventListener('pagehide', this.endOnPageHide)
  }

  private startHeartbeat() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = setInterval(this.beat, HEARTBEAT_INTERVAL_MS)
  }

  private async beat() {
    const sessionId = this.sessionId
    if (sessionId === null) return

    try {
      const response = await authorizedGraphQLRequest<{ heartbeatRoleplaySession: { open: boolean; remainingSeconds: number } }>(HEARTBEAT, { sessionId })
      if (this.sessionId !== sessionId) return

      const { open, remainingSeconds } = response.heartbeatRoleplaySession
      // Closed under us (another tab started a roleplay, or the reaper): run the
      // countdown out so the roleplay ends through its usual path, review included.
      const serverRemaining = open ? remainingSeconds : 0

      if (!module2Store.isTimerActive) return
      if (!open || Math.abs(serverRemaining - module2Store.remainingTime) > DRIFT_TOLERANCE_SECONDS) {
        // The countdown ends the roleplay on the tick that reaches zero, so leave it one.
        module2Store.setRemainingTime(Math.max(1, serverRemaining))
      }
    } catch (error) {
      // A missed beat is not fatal; the server allows 75 s of silence.
      console.error('Roleplay heartbeat failed:', error)
    }
  }

  /**
   * The page is going away and an awaited request would be cancelled with it.
   * `keepalive` lets this one outlive the page; the reaper covers the case where it
   * does not.
   */
  private endOnPageHide() {
    const sessionId = this.sessionId
    if (sessionId === null) return
    this.clearSession()

    const token = localStorage.getItem('auth_token')
    fetch(import.meta.env.VITE_API_GRAPHQL_URL || '', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ query: END_MUTATION, variables: { sessionId } }),
    }).catch(() => {})
  }
}

export const meteringStore = new MeteringStore()
